/**
 * Ticket Status Badge Component
 *
 * Displays ticket status with appropriate color coding.
 * Uses existing Badge component with semantic colors.
 */

import React from 'react';
import { Badge } from '../common/Badge';
import { TicketStatus } from '../../types/support';
import type { BadgeProps } from '../common/Badge';

export interface TicketStatusBadgeProps {
  /** Ticket status to display */
  status?: TicketStatus;

  /** Badge size */
  size?: BadgeProps['size'];

  /** Show status dot indicator */
  dot?: boolean;
}

/**
 * Maps ticket status to badge variant and label
 */
const getStatusConfig = (status: TicketStatus): { variant: BadgeProps['variant']; label: string } => {
  switch (status) {
    case TicketStatus.OPEN:
      return { variant: 'warning', label: 'Open' };
    case TicketStatus.IN_PROGRESS:
      return { variant: 'info', label: 'In Progress' };
    case TicketStatus.WAITING_CUSTOMER:
      return { variant: 'warning', label: 'Waiting for You' };
    case TicketStatus.WAITING_INTERNAL:
      return { variant: 'default', label: 'Waiting for Team' };
    case TicketStatus.RESOLVED:
      return { variant: 'success', label: 'Resolved' };
    case TicketStatus.CLOSED:
      return { variant: 'default', label: 'Closed' };
    default:
      return { variant: 'default', label: 'Unknown' };
  }
};

export const TicketStatusBadge: React.FC<TicketStatusBadgeProps> = ({
  status,
  size = 'md',
  dot = false,
}) => {
  if (!status) {
    return null;
  }

  const { variant, label } = getStatusConfig(status);

  return (
    <Badge variant={variant} size={size} dot={dot}>
      {label}
    </Badge>
  );
};
